/* ─────────────────────────────────────────────────────────────────────────
   Main product behaviours.
   - Gallery view switching ([data-view] thumbs → [data-view-panel] stages)
   - Variant select → price / availability updates + gallery swap when the
     variant has a featured media (lean JSON map in [data-product-json])
   - Add-to-bag via window.PavosCart.add (FormData), with "Added" flash
   - Sticky mobile CTA submits the same form (form="" attribute)
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var ADDED_FLASH_MS = 2000;

  /* Activate one gallery view by its [data-view-panel] id and sync the
     matching thumbnail's active state. Shared by thumb clicks and variant
     changes. */
  function activateView(root, id) {
    var panels = root.querySelectorAll('[data-view-panel]');
    var thumbs = root.querySelectorAll('[data-view]');
    panels.forEach(function (panel) {
      var active = panel.getAttribute('data-view-panel') === id;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    thumbs.forEach(function (thumb) {
      var active = thumb.getAttribute('data-view') === id;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  /* Reorder + filter the gallery to a variant's scope: that variant's own
     photo leads, every other variant's dedicated photo is hidden, and
     shared photos (data-media-id not in galleryOrder at all is never the
     case for shared photos — they're always included server-side, see
     main-product.liquid) follow in order. Only touches [data-view-panel]
     /[data-view] nodes that came from real product photos
     (.pdp__view--photo / thumbs with a matching data-media-id); the
     synthetic candle-visual panels (unlit/room) are untouched. */
  function applyGalleryOrder(root, galleryOrder) {
    if (!galleryOrder || !galleryOrder.length) return;

    var stage = root.querySelector('.pdp__stage');
    var thumbsWrap = root.querySelector('.pdp__thumbs');
    if (!stage) return;

    var orderStrings = galleryOrder.map(String);

    function reorderGroup(container, ariaAttr, ariaHiddenValue) {
      if (!container) return [];
      var nodes = Array.prototype.slice.call(
        container.querySelectorAll('[data-media-id]')
      );
      if (!nodes.length) return [];

      var byId = {};
      nodes.forEach(function (node) {
        byId[node.getAttribute('data-media-id')] = node;
      });

      /* Move matching nodes to the front, in galleryOrder's order. */
      var ordered = [];
      orderStrings.forEach(function (id) {
        var node = byId[id];
        if (node) {
          container.appendChild(node);
          ordered.push(node);
        }
      });

      /* Anything with a data-media-id not in this variant's order is some
         other variant's own photo — hide it; it stays out of rotation
         until that variant is picked again. */
      nodes.forEach(function (node) {
        var id = node.getAttribute('data-media-id');
        var inOrder = orderStrings.indexOf(id) !== -1;
        node.hidden = !inOrder;
        if (!inOrder) {
          node.classList.remove('is-active');
          if (ariaAttr) node.setAttribute(ariaAttr, ariaHiddenValue);
        }
      });

      return ordered;
    }

    var orderedPanels = reorderGroup(stage, 'aria-hidden', 'true');
    reorderGroup(thumbsWrap, 'aria-pressed', 'false');

    if (orderedPanels.length) {
      activateView(root, orderedPanels[0].getAttribute('data-view-panel'));
    }
  }

  /* Swap the gallery stage to a variant's featured media when no fuller
     galleryOrder is available (e.g. a future block providing partial
     variant data). Prefers the photo panel rendered for that exact media
     id; otherwise retargets the first photo panel's <img>. No-ops
     gracefully when the gallery only has synthetic candle-visual views
     and no photo panel exists. */
  function showVariantMedia(root, media) {
    if (!media || !media.src) return;
    var target = root.querySelector(
      '[data-view-panel][data-media-id="' + media.id + '"]'
    );
    if (!target) {
      target = root.querySelector('.pdp__view--photo');
      if (!target) return;
      var img = target.querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = media.src;
        if (media.alt) img.alt = media.alt;
      }
    }
    activateView(root, target.getAttribute('data-view-panel'));
  }

  function readVariantData(root) {
    var el = root.querySelector('[data-product-json]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function initGallery(root) {
    var thumbs = root.querySelectorAll('[data-view]');
    var panels = root.querySelectorAll('[data-view-panel]');
    if (!thumbs.length || !panels.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activateView(root, thumb.getAttribute('data-view'));
      });
    });
  }

  function initVariants(root) {
    var select = root.querySelector('[data-variant-select]');
    if (!select) return;

    var variants = readVariantData(root);
    var priceEl = root.querySelector('[data-price]');
    var compareEl = root.querySelector('[data-compare]');
    var stickyPriceEl = root.querySelector('[data-sticky-price]');
    var swatches = root.querySelectorAll('[data-swatch]');
    var buttons = root.querySelectorAll('[data-add-to-cart]');

    /* Shared by both the swatch click handler and the hidden select's own
       change event, so price / availability / gallery stay in sync no
       matter which one drives the update. */
    function applyOption(option) {
      if (!option) return;

      if (priceEl && option.dataset.price) {
        priceEl.textContent = option.dataset.price;
      }
      if (stickyPriceEl && option.dataset.price) {
        stickyPriceEl.textContent = option.dataset.price;
      }
      if (compareEl) {
        if (option.dataset.compare) {
          compareEl.querySelector('s').textContent = option.dataset.compare;
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.setAttribute('hidden', 'hidden');
        }
      }

      var available = option.dataset.available !== 'false';
      buttons.forEach(function (btn) {
        btn.disabled = !available;
        btn.textContent = available
          ? btn.getAttribute('data-label')
          : btn.getAttribute('data-soldout-label');
      });

      /* Gallery follows the variant: reorder to lead with this variant's
         own photo and hide every other variant's dedicated photo, using
         the order Liquid already computed server-side for each variant.
         Falls back to swapping just the stage image if, for any reason,
         galleryOrder isn't present on this variant's data. */
      var variant = null;
      for (var i = 0; i < variants.length; i += 1) {
        if (String(variants[i].id) === option.value) {
          variant = variants[i];
          break;
        }
      }
      if (variant && variant.galleryOrder && variant.galleryOrder.length) {
        applyGalleryOrder(root, variant.galleryOrder);
      } else if (variant && variant.featured_media) {
        showVariantMedia(root, variant.featured_media);
      }
    }

    function selectVariant(variantId) {
      var option = select.querySelector('option[value="' + variantId + '"]');
      if (!option || option.selected) return;

      select.value = variantId;

      swatches.forEach(function (swatch) {
        var active = swatch.getAttribute('data-variant-id') === String(variantId);
        swatch.classList.toggle('is-selected', active);
        swatch.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      applyOption(option);
    }

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        if (swatch.classList.contains('is-soldout')) return;
        selectVariant(swatch.getAttribute('data-variant-id'));
      });
    });

    /* Keeps anything else that reads the native select (or a future
       block/app relying on its change event) working unchanged. */
    select.addEventListener('change', function () {
      applyOption(select.options[select.selectedIndex]);
    });
  }

  function initForm(root) {
    var form = root.querySelector('[data-product-form]');
    if (!form) return;

    var buttons = root.querySelectorAll('[data-add-to-cart]');
    var timer = null;

    function flashAdded() {
      buttons.forEach(function (btn) {
        btn.textContent = btn.getAttribute('data-added-label');
      });
      clearTimeout(timer);
      timer = setTimeout(function () {
        buttons.forEach(function (btn) {
          if (!btn.disabled) btn.textContent = btn.getAttribute('data-label');
        });
      }, ADDED_FLASH_MS);
    }

    form.addEventListener('submit', function (event) {
      if (!window.PavosCart || typeof window.PavosCart.add !== 'function') {
        return; /* no helper — let the form post to /cart/add natively */
      }
      event.preventDefault();

      buttons.forEach(function (btn) { btn.disabled = true; });

      window.PavosCart.add(new FormData(form))
        .then(function () {
          buttons.forEach(function (btn) { btn.disabled = false; });
          flashAdded();
        })
        .catch(function () {
          buttons.forEach(function (btn) {
            btn.disabled = false;
            btn.textContent = btn.getAttribute('data-label');
          });
        });
    });
  }

  function initSection(root) {
    if (!root || root.hasAttribute('data-pdp-ready')) return;
    root.setAttribute('data-pdp-ready', '');
    initGallery(root);
    initVariants(root);
    initForm(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pdp]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
